import { Fragment } from 'react';

import classNames from 'classnames';

import { Dropdown, DropdownMenuItem } from '@/components/dropdown';
import { InteractableProps } from '@/explore/utils/interactable';

import { GroupedLegendData, LegendData, LegendItem } from './types';

import styles from './legend.module.scss';

type SharedProps = {
  onHighlight?: (seriesKey: string) => void;
  onHighlightEnd?: () => void;
  buildSeriesMenu?: (seriesKey: string) => DropdownMenuItem[];
};

type LegendProps = SharedProps & {
  data: LegendData;
};
export const Legend = ({ data, ...props }: LegendProps) =>
  data.type === 'basic' ? (
    <BasicLegend items={data.items} {...props} />
  ) : data.type === 'grouped' ? (
    <GroupedLegend data={data} {...props} />
  ) : null;

type BasicLegendProps = SharedProps & {
  items: LegendItem[];
};

const BasicLegend = ({ items, buildSeriesMenu, onHighlight, onHighlightEnd }: BasicLegendProps) => {
  return (
    <div className={styles.legend}>
      {items.map((item, idx) => (
        <Dropdown
          key={idx}
          align="left"
          trigger={(isOpen, setIsOpen) => (
            <button
              {...InteractableProps}
              className={classNames(styles.legendItem, {
                [styles.clickable]: buildSeriesMenu !== undefined,
              })}
              title={item.label}
              onMouseEnter={() => onHighlight?.(item.key)}
              onMouseLeave={onHighlightEnd}
              onClick={() => setIsOpen(!isOpen)}>
              <div className={styles.legendColor} style={{ backgroundColor: item.color }} />
              <div className={styles.legendLabel}>{item.label}</div>
            </button>
          )}
          items={buildSeriesMenu?.(item.key) ?? []}
        />
      ))}
    </div>
  );
};

type GroupedLegendProps = SharedProps & {
  data: GroupedLegendData;
};

export const GroupedLegend = ({ data, buildSeriesMenu }: GroupedLegendProps) => (
  <div className={styles.groupedLegend}>
    {data.groups.map((group, groupIdx) =>
      'items' in group ? (
        <div className={styles.groupItem} key={groupIdx}>
          <div className={styles.groupLabel} title={group.label}>
            {group.label}
          </div>
          <BasicLegend items={group.items} buildSeriesMenu={buildSeriesMenu} />
        </div>
      ) : (
        <div className={styles.groupItem} key={groupIdx}>
          <div className={styles.groupLabel} title={group.label}>
            {group.label}
          </div>
          <div className={styles.valueItems}>
            {group.groups.map(({ label, items }, groupIdx) => (
              <Fragment key={groupIdx}>
                <div className={styles.valueLabel} title={label}>
                  {label}
                </div>
                <BasicLegend items={items} buildSeriesMenu={buildSeriesMenu} />
              </Fragment>
            ))}
          </div>
        </div>
      ),
    )}
  </div>
);
